import { useState, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

// ── Palette derived from #0a2352 (navy) and #ff5c18 (orange) ──
// Navy tints: 50→900
// N50  #e8edf5  N100 #c0cce3  N200 #98abd1  N400 #4d6aad  N600 #1e3d7a  N800 #0a2352  N900 #061630
// Orange tints:
// O50  #fff1eb  O100 #ffc9af  O200 #ff9f73  O400 #ff5c18  O600 #cc4a13  O800 #7a2c0b  O900 #3d1606
// Neutral warm grays from navy undertone:
// G50  #f4f5f8  G100 #dde0e9  G200 #bcc1d0  G400 #8892a8  G600 #4f5a72  G800 #2a3347  G900 #131929

const C = {
  navy50:  "#e8edf5", navy100: "#c0cce3", navy200: "#98abd1",navy165:"#fca311",
  navy400: "#4d6aad", navy600: "#1e3d7a", navy800: "#0a2352", navy900: "#061630",
  org50:   "#fff1eb", org100:  "#ffc9af", org200:  "#ff9f73",
  org400:  "#ff5c18", org600:  "#cc4a13", org800:  "#7a2c0b", org900:  "#3d1606",
  g50:     "#f4f5f8", g100:    "#dde0e9", g200:    "#bcc1d0",
  g400:    "#8892a8", g600:    "#4f5a72", g800:    "#2a3347", g900:    "#131929",
  // semantic greens / amber kept minimal, navy-tinted
  green50: "#eaf3ee", green200:"#7fc99a", green600:"#1e7a45", green800:"#0e4526",
  amber50: "#fdf5e6", amber200:"#f5c96a", amber600:"#b87f10", amber800:"#6b4a07",
  red50:   "#fceaea", red200:  "#f09090", red600:  "#b83232", red800:  "#6b1a1a",
};

const STATUS_COLORS = {
  "Not Arrived": { bg: C.g50,      text: C.g600,      border: C.g200      },
  "Checked In":  { bg: C.navy50,   text: C.navy800,   border: C.navy200   },
  "Checked Out": { bg: C.green50,  text: C.green600,  border: C.green200  },
  "Overstay":    { bg: C.org50,    text: C.org600,    border: C.org200    },
};

const TYPE_COLORS = {
  Student:    { bg: C.navy50,  color: C.navy800,  border: C.navy200  },
  Faculty:    { bg: C.g50,     color: C.g800,     border: C.g200     },
  Management: { bg: C.org50,   color: C.org800,   border: C.org200   },
  Influencer: { bg: C.amber50, color: C.amber800, border: C.amber200 },
  Delegate:   { bg: C.green50, color: C.green800, border: C.green200 },
};

const StatusBadge = ({ status }) => {
  const c = STATUS_COLORS[status] || STATUS_COLORS["Not Arrived"];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: c.bg, color: c.text, border: `1px solid ${c.border}`,
      borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 600, letterSpacing: 0.2,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: c.border, display: "inline-block" }} />
      {status}
    </span>
  );
};

const initialRows = [
  { id: 1,  name: "Arjun Mehta",     ticket: "TKT-001", type: "Student",    phone: "+91 98765 43210", date: "2025-06-02", entryTime: null,       exitTime: null,       status: "Not Arrived" },
  { id: 2,  name: "Priya Sharma",    ticket: "TKT-002", type: "Faculty",    phone: "+91 91234 56789", date: "2025-06-02", entryTime: "09:15 AM", exitTime: "11:30 AM", status: "Checked Out" },
  { id: 3,  name: "Rahul Nair",      ticket: "TKT-003", type: "Delegate",   phone: "+91 99887 65432", date: "2025-06-02", entryTime: "09:45 AM", exitTime: null,       status: "Checked In"  },
  { id: 4,  name: "Sneha Iyer",      ticket: "TKT-004", type: "Student",    phone: "+91 80001 23456", date: "2025-06-02", entryTime: null,       exitTime: null,       status: "Not Arrived" },
  { id: 5,  name: "Vikram Rao",      ticket: "TKT-005", type: "Influencer", phone: "+91 70098 76543", date: "2025-06-02", entryTime: "10:05 AM", exitTime: null,       status: "Checked In"  },
  { id: 6,  name: "Ananya Krishnan", ticket: "TKT-006", type: "Student",    phone: "+91 81122 33445", date: "2025-06-02", entryTime: "08:50 AM", exitTime: "12:10 PM", status: "Checked Out" },
  { id: 7,  name: "Deepak Verma",    ticket: "TKT-007", type: "Management", phone: "+91 90011 22334", date: "2025-06-02", entryTime: null,       exitTime: null,       status: "Not Arrived" },
  { id: 8,  name: "Kavya Reddy",     ticket: "TKT-008", type: "Delegate",   phone: "+91 78899 00112", date: "2025-06-02", entryTime: "09:30 AM", exitTime: null,       status: "Checked In"  },
  { id: 9,  name: "Siddharth Menon", ticket: "TKT-009", type: "Student",    phone: "+91 88765 12345", date: "2025-06-02", entryTime: null,       exitTime: null,       status: "Not Arrived" },
  { id: 10, name: "Lakshmi Patel",   ticket: "TKT-010", type: "Influencer", phone: "+91 92233 44556", date: "2025-06-02", entryTime: "10:20 AM", exitTime: null,       status: "Checked In"  },
  { id: 11, name: "Rohan Gupta",     ticket: "TKT-011", type: "Faculty",    phone: "+91 97654 32109", date: "2025-06-02", entryTime: "09:00 AM", exitTime: "10:45 AM", status: "Checked Out" },
  { id: 12, name: "Meera Pillai",    ticket: "TKT-012", type: "Management", phone: "+91 83344 55667", date: "2025-06-02", entryTime: "10:50 AM", exitTime: null,       status: "Checked In"    },
];

const getNow = () => {
  const now = new Date();
  let h = now.getHours();
  const m = String(now.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${String(h).padStart(2, "0")}:${m} ${ampm}`;
};

const fmtDate = (d) => {
  if (!d) return "—";
  const [y, mo, day] = d.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${day} ${months[parseInt(mo)-1]} ${y}`;
};

export default function EventEntryExit() {
  const [rows, setRows]       = useState(initialRows);
  const [selected, setSelected] = useState(null);
  const [form, setForm]       = useState({ date: "", entryTime: "", exitTime: "" });
  const [search, setSearch]   = useState("");
  const gridRef = useRef(null);

  const filtered = rows.filter((r) =>
    r.name.toLowerCase().includes(search.toLowerCase()) ||
    r.ticket.toLowerCase().includes(search.toLowerCase()) ||
    r.type.toLowerCase().includes(search.toLowerCase())
  );

  const stats = {
    total:      rows.length,
    checkedIn:  rows.filter((r) => r.status === "Checked In").length,
    checkedOut: rows.filter((r) => r.status === "Checked Out").length,
    notArrived: rows.filter((r) => r.status === "Not Arrived").length,
  };

  const openModal = (row) => {
    setSelected(row);
    setForm({ date: row.date || "", entryTime: row.entryTime || "", exitTime: row.exitTime || "" });
  };
  const closeModal = () => setSelected(null);

  const handleAutoFill = (field) => setForm((f) => ({ ...f, [field]: getNow() }));
  const handleClear    = (field) => setForm((f) => ({ ...f, [field]: "" }));

  const computeStatus = (entry, exit) => {
    if (!entry) return "Not Arrived";
    if (!exit)  return "Checked In";
    return "Checked Out";
  };

  const handleSave = () => {
    const newStatus = computeStatus(form.entryTime, form.exitTime);
    setRows((prev) => prev.map((r) =>
      r.id === selected.id
        ? { ...r, date: form.date || null, entryTime: form.entryTime || null, exitTime: form.exitTime || null, status: newStatus }
        : r
    ));
    closeModal();
  };

  const columnDefs = [
    {
      headerName: "#", valueGetter: "node.rowIndex + 1", width: 50,
      cellStyle: { color: C.g400, fontSize: 12 }, sortable: false, filter: false,
    },
    {
      field: "ticket", headerName: "Ticket", width: 105,
      cellStyle: { fontFamily: "monospace", fontSize: 13, color: C.navy800, fontWeight: 700 },
    },
    { field: "name", headerName: "Attendee", flex: 1, minWidth: 150, cellStyle: { fontWeight: 500, color: C.g900 } },
    {
      field: "type", headerName: "Type", width: 110,
      cellRenderer: ({ value }) => {
        const s = TYPE_COLORS[value] || TYPE_COLORS.Student;
        return (
          <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, borderRadius: 12, padding: "2px 9px", fontSize: 11, fontWeight: 700 }}>
            {value}
          </span>
        );
      },
    },
    {
      field: "date", headerName: "Date", width: 120,
      cellRenderer: ({ value }) => value
        ? <span style={{ fontSize: 12, color: C.g600, fontWeight: 500 }}>{fmtDate(value)}</span>
        : <span style={{ color: C.g200, fontSize: 12 }}>—</span>,
    },
    {
      field: "entryTime", headerName: "Entry", width: 110,
      cellRenderer: ({ value }) => value
        ? <span style={{ color: C.navy800, fontWeight: 700, fontSize: 13 }}><span style={{ marginRight: 3, color: C.org400 }}>→</span>{value}</span>
        : <span style={{ color: C.g200, fontSize: 12 }}>—</span>,
    },
    {
      field: "exitTime", headerName: "Exit", width: 110,
      cellRenderer: ({ value }) => value
        ? <span style={{ color: C.green600, fontWeight: 700, fontSize: 13 }}><span style={{ marginRight: 3 }}>←</span>{value}</span>
        : <span style={{ color: C.g200, fontSize: 12 }}>—</span>,
    },
    {
      field: "status", headerName: "Status", width: 140,
      cellRenderer: ({ value }) => <StatusBadge status={value} />,
    },
    {
      headerName: "", width: 80, sortable: false, filter: false,
      cellRenderer: ({ data }) => (
        <button
          onClick={(e) => { e.stopPropagation(); openModal(data); }}
          style={{
            background: "none", border: `1px solid ${C.g100}`, borderRadius: 6,
            padding: "3px 10px", cursor: "pointer", fontSize: 12, color: C.g600, fontWeight: 500,
          }}
          onMouseEnter={(e) => { e.target.style.background = C.navy50; e.target.style.borderColor = C.navy200; e.target.style.color = C.navy800; }}
          onMouseLeave={(e) => { e.target.style.background = "none"; e.target.style.borderColor = C.g100; e.target.style.color = C.g600; }}
        >Edit</button>
      ),
    },
  ];

  // ── input style helpers ──
  const inputBase = (active, accentBorder, accentBg, accentText) => ({
    flex: 1, borderRadius: 8, padding: "9px 13px",
    fontSize: 14, fontWeight: 600, outline: "none", letterSpacing: 0.3,
    border:     `1.5px solid ${active ? accentBorder : C.g100}`,
    color:      active ? accentText : C.g400,
    background: active ? accentBg   : C.g50,
    cursor:     active ? "text" : "not-allowed",
  });

  const nowBtn = (active, accentBg, accentBorder, accentText, hoverBg) => ({
    base: {
      borderRadius: 8, padding: "9px 13px", cursor: active ? "pointer" : "not-allowed",
      fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", transition: "background 0.15s",
      border:      `1.5px solid ${active ? accentBorder : C.g100}`,
      background:  active ? accentBg  : C.g50,
      color:       active ? accentText : C.g400,
    },
    hoverBg,
    defaultBg: accentBg,
  });

  const entryBtn  = nowBtn(true,             C.navy50,  C.navy200,  C.navy800, C.navy100);
  const exitBtn   = nowBtn(!!form.entryTime, C.org50,   C.org200,   C.org600,  C.org100);

  return (
    <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", background: C.g50, minHeight: "100vh", padding: "24px 28px" }}>
      <h2 className="pt-1 pb-1 text-center" style={{ color: "#4969ab", fontSize: '18px' }}>
        Event Gate Management
      </h2>
      {/* ── Top bar ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>

        <input
          placeholder="Search attendee, ticket, type…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            border: `1px solid ${C.g100}`, borderRadius: 8, padding: "8px 14px",
            fontSize: 13, background: "#fff", color: C.g900, width: 230, outline: "none",
          }}
        />
      </div>

      {/* ── Stats ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Total Registered", val: stats.total,      bg: C.navy165, text: "#fff",      sub: "rgba(255,255,255,0.6)" },
          { label: "Checked In",       val: stats.checkedIn,  bg: C.navy50,  text: C.navy800,   sub: C.navy400,  border: C.navy100 },
          { label: "Checked Out",      val: stats.checkedOut, bg: C.green50, text: C.green800,  sub: C.green600, border: C.green200 },
          { label: "Not Arrived",      val: stats.notArrived, bg: C.org50,   text: C.org800,    sub: C.org600,   border: C.org200 },
        ].map((s) => (
          <div key={s.label} style={{
            background: s.bg, border: s.border ? `1px solid ${s.border}` : "none",
            borderRadius: 10, padding: "14px 18px",
          }}>
            <div style={{ fontSize: 12, color: s.sub, marginBottom: 5, fontWeight: 500 }}>{s.label}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: s.text, lineHeight: 1 }}>{s.val}</div>
          </div>
        ))}
      </div>

      {/* ── Grid ── */}
      <div className="ag-theme-alpine" style={{ borderRadius: 12, overflow: "hidden", border: `1px solid ${C.g100}`, boxShadow: "0 1px 6px rgba(10,35,82,0.07)" }}>
        <style>{`
          .ag-theme-alpine {
            --ag-font-family: 'DM Sans','Segoe UI',sans-serif;
            --ag-font-size: 13px;
            --ag-header-background-color: ${C.navy800};
            --ag-header-foreground-color: ${C.navy100};
            --ag-row-hover-color: ${C.navy50};
            --ag-selected-row-background-color: ${C.navy50};
            --ag-border-color: ${C.g100};
            --ag-cell-horizontal-padding: 14px;
            --ag-row-height: 48px;
            --ag-header-height: 42px;
            --ag-background-color: #fff;
            --ag-odd-row-background-color: #fff;
          }
          .ag-header-cell-label { font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: 0.6px; }
          .ag-row { cursor: pointer; border-bottom: 1px solid ${C.g50} !important; }
          .ag-header-cell { border-right: 1px solid ${C.navy600} !important; }
        `}</style>
        <AgGridReact
          ref={gridRef}
          rowData={filtered}
          columnDefs={columnDefs}
          domLayout="autoHeight"
          rowSelection="single"
          onRowClicked={({ data }) => openModal(data)}
          suppressCellFocus
          animateRows
          getRowId={({ data }) => String(data.id)}
        />
      </div>

      <div style={{ marginTop: 8, fontSize: 11, color: C.g400, textAlign: "right" }}>
        Click any row or use Edit to log entry / exit
      </div>

      {/* ── Modal ── */}
      {selected && (
        <div
          onClick={closeModal}
          style={{ position: "fixed", inset: 0, background: "rgba(10,35,82,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: 16, width: 440, maxWidth: "95vw", boxShadow: "0 12px 48px rgba(10,35,82,0.22)", overflow: "hidden" }}
          >
            {/* Modal header */}
            <div style={{ background: C.navy800, padding: "18px 22px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>{selected.name}</div>
                <div style={{ fontSize: 12, color: C.navy200, marginTop: 3, display: "flex", gap: 10, alignItems: "center" }}>
                  <span style={{ fontFamily: "monospace", color: C.org400, fontWeight: 700 }}>{selected.ticket}</span>
                  <span style={{ color: C.navy400 }}>·</span>
                  <span>{selected.phone}</span>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <StatusBadge status={computeStatus(form.entryTime, form.exitTime)} />
                <button onClick={closeModal} style={{ background: "none", border: "none", cursor: "pointer", color: C.navy200, fontSize: 22, lineHeight: 1, padding: "0 2px" }}>×</button>
              </div>
            </div>

            {/* Orange accent bar */}
            <div style={{ height: 3, background: `linear-gradient(90deg, ${C.org400}, ${C.org200})` }} />

            {/* Modal body */}
            <div style={{ padding: "22px 22px 20px" }}>

              {/* Date field */}
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: C.g600, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 7 }}>
                  Event Date
                </label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                  style={{
                    width: "100%", border: `1.5px solid ${C.g100}`, borderRadius: 8,
                    padding: "9px 13px", fontSize: 13, color: C.g900, background: C.g50,
                    outline: "none", boxSizing: "border-box",
                  }}
                />
              </div>

              {/* Entry Time */}
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: C.g600, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 7 }}>
                  Entry Time
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    type="text"
                    placeholder="e.g. 10:30 AM"
                    value={form.entryTime}
                    onChange={(e) => setForm((f) => ({ ...f, entryTime: e.target.value }))}
                    style={inputBase(true, C.navy200, C.navy50, C.navy800)}
                  />
                  <button
                    onClick={() => handleAutoFill("entryTime")}
                    style={entryBtn.base}
                    onMouseEnter={(e) => e.currentTarget.style.background = entryBtn.hoverBg}
                    onMouseLeave={(e) => e.currentTarget.style.background = entryBtn.defaultBg}
                  >⏱ Now</button>
                  {form.entryTime && (
                    <button onClick={() => handleClear("entryTime")} style={{ background: "none", border: `1px solid ${C.g100}`, borderRadius: 8, padding: "9px 10px", cursor: "pointer", color: C.g400, fontSize: 13 }}>✕</button>
                  )}
                </div>
                {form.entryTime && <div style={{ marginTop: 5, fontSize: 11, color: C.navy400, fontWeight: 600 }}>✓ Entry recorded</div>}
              </div>

              {/* Exit Time */}
              <div style={{ marginBottom: 22 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: C.g600, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 7 }}>
                  Exit Time
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    type="text"
                    placeholder="e.g. 02:15 PM"
                    value={form.exitTime}
                    onChange={(e) => setForm((f) => ({ ...f, exitTime: e.target.value }))}
                    disabled={!form.entryTime}
                    style={inputBase(!!form.entryTime, C.org200, C.org50, C.org600)}
                  />
                  <button
                    onClick={() => form.entryTime && handleAutoFill("exitTime")}
                    disabled={!form.entryTime}
                    style={exitBtn.base}
                    onMouseEnter={(e) => form.entryTime && (e.currentTarget.style.background = exitBtn.hoverBg)}
                    onMouseLeave={(e) => form.entryTime && (e.currentTarget.style.background = exitBtn.defaultBg)}
                  >⏱ Now</button>
                  {form.exitTime && (
                    <button onClick={() => handleClear("exitTime")} style={{ background: "none", border: `1px solid ${C.g100}`, borderRadius: 8, padding: "9px 10px", cursor: "pointer", color: C.g400, fontSize: 13 }}>✕</button>
                  )}
                </div>
                {!form.entryTime
                  ? <div style={{ marginTop: 5, fontSize: 11, color: C.g400 }}>Set entry time first</div>
                  : form.exitTime && <div style={{ marginTop: 5, fontSize: 11, color: C.org600, fontWeight: 600 }}>✓ Exit recorded</div>
                }
              </div>

              {/* Duration */}
              {form.entryTime && form.exitTime && (
                <div style={{ background: C.navy50, border: `1px solid ${C.navy100}`, borderRadius: 8, padding: "10px 14px", marginBottom: 18, fontSize: 13, color: C.navy800 }}>
                  <span style={{ fontWeight: 700 }}>Duration: </span>
                  <span style={{ color: C.org600, fontWeight: 700 }}>{form.entryTime}</span>
                  <span style={{ margin: "0 8px", color: C.g400 }}>→</span>
                  <span style={{ color: C.navy600, fontWeight: 700 }}>{form.exitTime}</span>
                </div>
              )}

              {/* Actions */}
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button
                  onClick={closeModal}
                  style={{ background: "none", border: `1px solid ${C.g100}`, borderRadius: 8, padding: "9px 20px", fontSize: 13, cursor: "pointer", color: C.g600, fontWeight: 500 }}
                >Cancel</button>
                <button
                  onClick={handleSave}
                  style={{ background: C.org400, border: "none", borderRadius: 8, padding: "9px 24px", fontSize: 13, cursor: "pointer", color: "#fff", fontWeight: 700, letterSpacing: 0.2, transition: "background 0.15s" }}
                  onMouseEnter={(e) => e.currentTarget.style.background = C.org600}
                  onMouseLeave={(e) => e.currentTarget.style.background = C.org400}
                >Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}